import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ComplianceTabProps {
  crawlId: string;
}

export function ComplianceTab({ crawlId }: ComplianceTabProps) {
  const query = useQuery({
    queryKey: queryKeys.crawls.report(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/report', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading compliance report…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red-600">Failed to load report.</p>;
  const report = query.data;

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <SectionCard title="VPPA" risk={report.vppa.risk} findings={report.vppa.findings} />
      <SectionCard title="GDPR" findings={report.gdpr.findings} />
      <SectionCard title="CCPA" findings={report.ccpa.findings} />
    </div>
  );
}

function SectionCard({
  title,
  risk,
  findings,
}: {
  title: string;
  risk?: 'low' | 'medium' | 'high' | 'unknown';
  findings: Array<{ id: string; severity: 'info' | 'warn' | 'error' | 'critical'; message: string }>;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        {risk && (
          <Badge variant={risk === 'high' ? 'destructive' : risk === 'medium' ? 'warning' : risk === 'low' ? 'success' : 'muted'}>
            {risk}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {findings.length === 0 ? (
          <p className="text-xs text-muted-foreground">No findings.</p>
        ) : (
          <ul className="flex flex-col gap-2 text-xs">
            {findings.map((f) => (
              <li key={f.id} className="flex items-start gap-2">
                <Badge
                  variant={
                    f.severity === 'critical' || f.severity === 'error'
                      ? 'destructive'
                      : f.severity === 'warn'
                        ? 'warning'
                        : 'info'
                  }
                >
                  {f.severity}
                </Badge>
                <span>{f.message}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
